import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

const STATUS_COLORS = {
  good: '#16a34a',
  fair: '#f59e0b',
  poor: '#dc2626',
};

const GRADE_COLORS = {
  A: '#2563eb',
  B: '#16a34a',
  C: '#f59e0b',
  D: '#f97316',
  F: '#dc2626',
};

function getScoreColor(score) {
  if (score <= 40) return '#dc2626';
  if (score <= 60) return '#f59e0b';
  if (score <= 80) return '#16a34a';
  return '#2563eb';
}

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '4px',
  },
  gaugeContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  componentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  componentCard: {
    background: '#f9fafb',
    padding: '12px',
    borderRadius: '6px',
  },
  componentName: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '4px',
  },
  componentScore: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '2px',
  },
  componentWeight: {
    fontSize: '10px',
    color: '#9ca3af',
    marginBottom: '6px',
  },
  progressBarBg: {
    height: '4px',
    background: '#e5e7eb',
    borderRadius: '2px',
    marginBottom: '6px',
    overflow: 'hidden',
  },
  componentDesc: {
    fontSize: '10px',
    color: '#6b7280',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  summary: {
    fontSize: '13px',
    color: '#4b5563',
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  empty: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

function drawGauge(svgEl, score, grade) {
  const width = 300;
  const height = 180;
  const cx = width / 2;
  const cy = height - 10;
  const outerRadius = 120;
  const arcWidth = 20;
  const innerRadius = outerRadius - arcWidth;

  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg.attr('width', width).attr('height', height);

  const g = svg.append('g').attr('transform', `translate(${cx}, ${cy})`);

  const arcGenerator = d3.arc()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .cornerRadius(2);

  // Background arc (full semicircle: -PI/2 to PI/2)
  g.append('path')
    .attr('d', arcGenerator({
      startAngle: -Math.PI / 2,
      endAngle: Math.PI / 2,
    }))
    .attr('fill', '#f3f4f6');

  // Score arc
  const scoreAngle = -Math.PI / 2 + (Math.PI * (score / 100));
  g.append('path')
    .attr('d', arcGenerator({
      startAngle: -Math.PI / 2,
      endAngle: scoreAngle,
    }))
    .attr('fill', getScoreColor(score));

  // Tick marks at 0, 25, 50, 75, 100
  const tickValues = [0, 25, 50, 75, 100];
  const tickOuter = outerRadius + 6;
  const tickInner = outerRadius + 1;
  const labelRadius = outerRadius + 18;

  tickValues.forEach((val) => {
    const angle = -Math.PI / 2 + (Math.PI * (val / 100));
    const x1 = tickInner * Math.cos(angle);
    const y1 = tickInner * Math.sin(angle);
    const x2 = tickOuter * Math.cos(angle);
    const y2 = tickOuter * Math.sin(angle);

    g.append('line')
      .attr('x1', x1).attr('y1', y1)
      .attr('x2', x2).attr('y2', y2)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5);

    const lx = labelRadius * Math.cos(angle);
    const ly = labelRadius * Math.sin(angle);

    g.append('text')
      .attr('x', lx)
      .attr('y', ly)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
      .text(val);
  });

  // Score number in center
  g.append('text')
    .attr('x', 0)
    .attr('y', -40)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '36px')
    .attr('font-weight', '700')
    .attr('fill', getScoreColor(score))
    .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .text(score);

  g.append('text')
    .attr('x', 0)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '16px')
    .attr('fill', '#9ca3af')
    .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .text('/100');

  // Grade circle
  const gradeColor = GRADE_COLORS[grade] || '#6b7280';
  g.append('circle')
    .attr('cx', 0)
    .attr('cy', 14)
    .attr('r', 20)
    .attr('fill', gradeColor);

  g.append('text')
    .attr('x', 0)
    .attr('y', 14)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('font-size', '18px')
    .attr('font-weight', '700')
    .attr('fill', '#ffffff')
    .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .text(grade);
}

export default function HealthScore({ data }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || !svgRef.current) return;
    drawGauge(svgRef.current, data.overallScore, data.grade);
  }, [data]);

  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Pricing Health Score</div>
          <div style={styles.subtitle}>
            Composite assessment of data quality, stability, and coverage
          </div>
        </div>
        <div style={styles.empty}>No health score data available</div>
      </div>
    );
  }

  const { components = [], summary } = data;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Pricing Health Score</div>
        <div style={styles.subtitle}>
          Composite assessment of data quality, stability, and coverage
        </div>
      </div>

      <div style={styles.gaugeContainer}>
        <svg ref={svgRef} />
      </div>

      {components.length > 0 && (
        <div style={styles.componentGrid}>
          {components.map((comp, idx) => {
            const statusColor = STATUS_COLORS[comp.status] || '#6b7280';
            return (
              <div
                key={idx}
                style={{
                  ...styles.componentCard,
                  borderTop: `3px solid ${statusColor}`,
                }}
              >
                <div style={styles.componentName}>{comp.name}</div>
                <div style={{ ...styles.componentScore, color: statusColor }}>
                  {comp.score}
                </div>
                <div style={styles.componentWeight}>
                  Weight: {Math.round(comp.weight * 100)}%
                </div>
                <div style={styles.progressBarBg}>
                  <div
                    style={{
                      height: '100%',
                      width: `${comp.score}%`,
                      background: statusColor,
                      borderRadius: '2px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <div style={styles.componentDesc}>{comp.description}</div>
              </div>
            );
          })}
        </div>
      )}

      {summary && <div style={styles.summary}>{summary}</div>}
    </div>
  );
}
